import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, RefreshCw, Unplug } from "lucide-react";
import {
  connectIntervals,
  disconnectIntervals,
  getIntervalsStatus,
  syncIntervals,
  type IntervalsStatus,
  type SyncResult,
} from "@/api/integrations";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, SectionLabel } from "@/components/ui/Card";
import { relativeDistance } from "@/lib/dates";
import { LoadError } from "@/components/ui/LoadError";
import { invalidateSyncData } from "@/lib/syncQueries";
import { useSyncTask } from "@/lib/useSyncTask";

export function IntervalsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { run, busy } = useSyncTask();
  const statusQ = useQuery<IntervalsStatus>({
    queryKey: ["intervals-status"],
    queryFn: ({ signal }) => getIntervalsStatus(signal),
    refetchOnWindowFocus: true,
  });

  const [athleteId, setAthleteId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [connectError, setConnectError] = useState<string | null>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  function sync() {
    run(async (signal, current) => {
      setSyncMsg(null); setSyncResult(null);
      try {
        const result = await syncIntervals(30, signal);
        if (!current()) return;
        setSyncResult(result);
        await invalidateSyncData(qc, current);
      } catch (error) {
        if (current()) setSyncMsg(workoutSyncFailureKey(error));
      }
    });
  }

  function disconnect() {
    run(async (signal, current) => {
      setSyncMsg(null);
      try {
        await disconnectIntervals(signal);
        if (!current()) return;
        setSyncResult(null);
        await invalidateSyncData(qc, current);
      } catch {
        if (current()) setSyncMsg("intervals_card.disconnect_failed");
      }
    });
  }

  function handleConnect(e: FormEvent) {
    e.preventDefault();
    setConnectError(null);
    if (!athleteId.trim() || !apiKey.trim()) {
      setConnectError("intervals_card.both_fields_required");
      return;
    }
    run(async (signal, current) => {
      try {
        await connectIntervals(athleteId.trim(), apiKey.trim(), signal);
        if (!current()) return;
        setAthleteId(""); setApiKey(""); setConnectError(null);
        await invalidateSyncData(qc, current);
      } catch (err) {
        if (!current()) return;
        const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
        setConnectError(detail === "intervals_credentials_rejected" ? "intervals_card.rejected_creds" : "intervals_card.connect_failed");
      }
    });
  }

  const status = statusQ.data;

  if (statusQ.isLoading) {
    return (
      <Card>
        <SectionLabel>{t("intervals_card.section_label")}</SectionLabel>
        <p className="mt-2 text-[13px] text-taupe">{t("intervals_card.loading_status")}</p>
      </Card>
    );
  }

  if (statusQ.isError || !status) {
    return <Card><SectionLabel>{t("intervals_card.section_label")}</SectionLabel>
      <LoadError onRetry={() => void statusQ.refetch()} retrying={statusQ.isFetching} />
      {syncResult && <WorkoutSyncResult result={syncResult} />}
      {syncMsg && <p role="alert" className="mt-3 text-sm text-coral">{t(syncMsg)}</p>}
    </Card>;
  }

  if (!status.connected) {
    return (
      <Card>
        <SectionLabel>{t("intervals_card.section_label")}</SectionLabel>
        <p className="mt-2 text-[13px] text-taupe">
          {t("intervals_card.connect_intro")}{" "}
          <a
            href="https://intervals.icu/settings"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-amber-dark hover:underline"
          >
            {t("intervals_card.connect_settings_link")}
          </a>{" "}
          {t("intervals_card.connect_intro_suffix")}
        </p>
        <form onSubmit={handleConnect} className="mt-3 space-y-3">
          <Input
            label={t("intervals_card.athlete_id_label")}
            placeholder="i12345"
            value={athleteId}
            onChange={(e) => setAthleteId(e.target.value)}
            autoComplete="off"
            disabled={busy}
            required
          />
          <Input
            label={t("intervals_card.api_key_label")}
            type="password"
            placeholder="xxxxxxxxxxxxxxxx"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="off"
            disabled={busy}
            required
          />
          {connectError ? (
            <div role="alert" className="rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
              {t(connectError)}
            </div>
          ) : null}
          <Button
            type="submit"
            size="md"
            loading={busy}
            className="w-full"
          >
            <Link2 className="h-4 w-4" /> {t("intervals_card.connect_button")}
          </Button>
          <p className="text-[11px] text-taupe">
            {t("intervals_card.fernet_note")}
          </p>
        </form>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{t("intervals_card.section_label")}</SectionLabel>
        <span className="rounded-full bg-amber/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-dark">
          ✓ {t("intervals_card.status_connected")}
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-[13px]">
        <div className="rounded-lg bg-cream px-3 py-2">
          <dt className="text-[11px] uppercase tracking-wider text-taupe">
            {t("intervals_card.athlete_id_label")}
          </dt>
          <dd className="metric-number text-[14px]">{status.athlete_id}</dd>
        </div>
        <div className="rounded-lg bg-cream px-3 py-2">
          <dt className="text-[11px] uppercase tracking-wider text-taupe">
            {t("intervals_card.last_sync_label")}
          </dt>
          <dd className="text-[13px] text-navy-deep">
            {status.last_synced_at ? relativeDistance(status.last_synced_at) : "—"}
          </dd>
        </div>
      </dl>
      {syncMsg ? (
        <div role="alert" className="mt-3 rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
          {t(syncMsg)}
        </div>
      ) : null}
      {syncResult && <WorkoutSyncResult result={syncResult} />}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button
          variant="secondary"
          size="md"
          onClick={sync}
          loading={busy}
        >
          <RefreshCw className="h-4 w-4" /> {t("intervals_card.sync_button")}
        </Button>
        <Button
          variant="ghost"
          size="md"
          onClick={() => {
            if (confirm(t("intervals_card.disconnect_confirm"))) {
              disconnect();
            }
          }}
          loading={busy}
        >
          <Unplug className="h-4 w-4" /> {t("intervals_card.disconnect_button")}
        </Button>
      </div>
      <p className="mt-3 text-[11px] text-taupe">
        {t("intervals_card.auto_sync_note")}
      </p>
    </Card>
  );
}

export function WorkoutSyncResult({ result }: { result: SyncResult }) {
  const { t } = useTranslation();
  return <div role="status" className="mt-3 space-y-1 rounded-lg bg-amber/10 px-3 py-2 text-[13px] text-amber-dark">
    <SectionLabel>{t("workouts.completed")}</SectionLabel>
    <p>{t("intervals_card.synced_msg", { new: result.new_workouts, updated: result.updated, total: result.total })}</p>
    {result.total === 0 && <p>{t("intervals_card.empty_window")}</p>}
    {!!result.skipped && <p>{t("intervals_card.skipped_msg", { count: result.skipped })}</p>}
    {!!result.restricted && <><p>{t("intervals_card.restricted_msg", { count: result.restricted })}</p><p>{t("intervals_card.source_restricted")}</p></>}
  </div>;
}

export function workoutSyncFailureKey(error: unknown) {
  const detail = (error as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
  return detail === "intervals_source_restricted" ? "intervals_card.source_restricted" : "intervals_card.sync_failed";
}
